import { Avatar } from '@seapoint/client/src/design-system/avatar/avatar/avatar';
import { Checkbox } from '@seapoint/client/src/design-system/checkbox/checkbox';
import { ColumnHeaderWithSorting } from '@seapoint/client/src/design-system/data-table/column-header-with-sorting';
import { DropdownMenu, DropdownMenuItem } from '@seapoint/client/src/design-system/dropdown-menu/dropdown-menu';
import { Input } from '@seapoint/client/src/design-system/input/input';
import { EmployeeView } from '@seapoint/client/src/types/entity-types';
import { cn } from '@seapoint/client/src/utils/cn';
import { getCurrencySymbol } from '@seapoint/client/src/utils/get-currency-symbol';
import { ColumnDef } from '@tanstack/react-table';
import { User } from 'lucide-react';
import { FormState } from 'react-hook-form';
import { PayrollFormData } from '../../financial-information-step/financial-information-form';

export type PayrollEmployee = EmployeeView & {
  netPay: string;
  reference: string;
};

export const payrollDraftTableColumnDefinition: ColumnDef<PayrollEmployee>[] = [
  {
    id: 'select',
    header: ({ table }) => {
      const allIds = table.getRowModel().rows.map((row) => row.original.id);
      const selected = table.options.meta?.selectedRowIds ?? [];
      const allSelected = allIds.length > 0 && allIds.every((id) => selected.includes(id));
      const someSelected = allIds.length > 0 && allIds.some((id) => selected.includes(id)) && !allSelected;

      return (
        <Checkbox
          checked={someSelected ? 'indeterminate' : allSelected}
          onCheckedChange={(checked) => {
            if (checked) {
              table.options.meta?.setSelectedRowIds?.(Array.from(new Set([...selected, ...allIds])));
            } else {
              table.options.meta?.setSelectedRowIds?.(selected.filter((id) => !allIds.includes(id)));
            }
          }}
          aria-label="Select all"
        />
      );
    },
    cell: ({ row, table }) => (
      <Checkbox
        checked={table.options.meta?.selectedRowIds?.includes(row.original.id)}
        onCheckedChange={(checked) => {
          const id = row.original.id;
          const selected = table.options.meta?.selectedRowIds ?? [];
          if (checked) {
            table.options.meta?.setSelectedRowIds?.([...selected, id]);
          } else {
            table.options.meta?.setSelectedRowIds?.(selected.filter((sid) => sid !== id));
          }
        }}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    size: 5,
    minSize: 5,
  },
  {
    id: 'avatar',
    header: () => null,
    cell: ({ row }) => {
      const employee = row.original;
      const fullName = `${employee.firstName} ${employee.lastName}`;
      return <Avatar name={fullName} size="sm" />;
    },
    enableSorting: false,
    enableHiding: false,
    size: 6,
    minSize: 6,
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="First Name" />,
    cell: ({ row }) => <span>{row.original.firstName}</span>,
    size: 15,
    minSize: 15,
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Last Name" />,
    cell: ({ row }) => <span>{row.original.lastName}</span>,
    size: 15,
    minSize: 15,
  },
  {
    accessorKey: 'bankAccount',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Bank Account" />,
    cell: ({ row, table }) => {
      const bankAccount = table.options.meta?.showAccountOptions
        ? (() => {
            const sortCode = row.original.destination?.sortCode;
            const formattedSortCode = sortCode?.replace(/(\d{2})(\d{2})(\d{2})/, '$1-$2-$3');
            const accountNumber = row.original.destination?.accountNumber;
            const displayValue = [formattedSortCode, accountNumber].join(' ');
            return displayValue;
          })()
        : row.original.destination?.iban;
      return <span className="text-sm">{bankAccount}</span>;
    },
    size: 25,
  },
  {
    accessorKey: 'reference',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Payment Reference" />,
    cell: ({ row }) => {
      return <span className="text-sm">{row.original.reference}</span>;
    },
    size: 20,
    minSize: 20,
  },
  {
    accessorKey: 'netPay',
    header: ({ column }) => (
      <div className="flex justify-end">
        <ColumnHeaderWithSorting column={column} label="Net Pay" />
      </div>
    ),
    cell: ({ row, table }) => {
      const employee = row.original;
      const currency = employee.destination?.currency;
      const currencySymbol = getCurrencySymbol(currency);
      const onNetPayChange = table.options.meta?.onNetPayChange;
      const formState = table.options.meta?.formState as FormState<PayrollFormData>;
      const isSelectedRecord = table.options.meta?.selectedRowIds?.includes(employee.id);
      const isValidField = Number(employee.netPay || 0) > 0;

      return (
        <Input
          type="text"
          disabled={!isSelectedRecord}
          value={employee.netPay ? `${currencySymbol}${employee.netPay}` : ''}
          onChange={(e) => {
            const value = e.target.value;
            // Remove currency symbol and non-numeric chars, then validate
            const stringValue = value.replace(currencySymbol, '').replace(/[^\d.]/g, '');
            // Allow empty string or valid decimal numbers with up to 2 decimal places
            if (stringValue === '' || /^\d*\.?\d{0,2}$/.test(stringValue)) {
              if (onNetPayChange) {
                onNetPayChange(employee.id, stringValue);
              }
            }
          }}
          onFocus={(e) => {
            if (e.target.value === currencySymbol + '0') {
              e.target.value = currencySymbol;
            }
          }}
          placeholder={`${currencySymbol}0.00`}
          className={cn(
            'w-full rounded-lg border-border bg-white text-right text-sm',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            { 'border-destructive': formState?.isSubmitted && isSelectedRecord && !isValidField },
          )}
          // Prevent row click when clicking on input
          onClick={(e) => e.stopPropagation()}
        />
      );
    },
    size: 15,
    minSize: 15,
  },
  {
    id: 'actions',
    cell: ({ row, table }) => {
      const employee = row.original;

      const menuItems: DropdownMenuItem[] = [
        {
          id: 'edit',
          label: 'Edit Employee',
          icon: <User size={16} />,
          onClick: () => table.options.meta?.onEditEmployee?.(employee.id),
        },
      ];

      return (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu items={menuItems} />
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
    size: 7,
    minSize: 7,
  },
];
